import { ArcanaIdentities } from '../arcana-identities';
import { RIDER_WAITE_IMAGE_URLS } from './rider-waite-images';
import type { DeckDefinition } from '../../../types/deck';

/** One bundled image candidate, looked up by exact file name. */
const img = (file: string) => ({ uri: RIDER_WAITE_IMAGE_URLS[file] });

/**
 * The Rider-Waite tarot (Smith-Waite) — drawn by Pamela Colman Smith and
 * published by William Rider & Son in 1909, under A. E. Waite's direction.
 * The first mass-market illustrated tarot and the visual ancestor of nearly
 * every deck printed since. Famously, Smith drew on the Sola Busca pips she
 * had likely seen as British Museum photographs, and adopted its
 * idiosyncratic Strength-at-eight ordering.
 *
 * Art: public-domain scans (1909 publication) bundled from
 * src/assets/images/rider-waite (see rider-waite-images.ts). The card back
 * (CardBacks.jpg) came with the uploaded set; back.jpg is the public-domain
 * "Roses and Lilies" pattern from Wikimedia Commons, kept as a fallback.
 */
export const RIDER_WAITE: DeckDefinition = {
    id: 'rider-waite',
    label: 'Rider Waite',
    header: {
        byline: "Pamela Colman Smith's",
        title: 'Rider-Waite Tarot',
        slogan: 'The visionary standard — every card tells the story...',
    },
    images: {
        [ArcanaIdentities.BACK]: [img('CardBacks.jpg'), img('back.jpg')],
        /* THELEMA is the engine's generic placeholder card — use the deck back. */
        [ArcanaIdentities.THELEMA]: [img('CardBacks.jpg')],

        /* Majors — Smith-Waite ordering (Strength at VIII, Justice at XI). */
        [ArcanaIdentities.ADJUSTMENT]: [img('11-Justice.jpg')],
        [ArcanaIdentities.LUST]: [img('08-Strength.jpg')],
        [ArcanaIdentities.THE_FOOL]: [img('00-TheFool.jpg')],
        [ArcanaIdentities.THE_MAGUS]: [img('01-TheMagician.jpg')],
        [ArcanaIdentities.THE_PRIESTESS]: [img('02-TheHighPriestess.jpg')],
        [ArcanaIdentities.THE_EMPRESS]: [img('03-TheEmpress.jpg')],
        [ArcanaIdentities.THE_EMPEROR]: [img('04-TheEmperor.jpg')],
        [ArcanaIdentities.THE_HIEROPHANT]: [img('05-TheHierophant.jpg')],
        [ArcanaIdentities.THE_LOVERS]: [img('06-TheLovers.jpg')],
        [ArcanaIdentities.THE_CHARIOT]: [img('07-TheChariot.jpg')],
        [ArcanaIdentities.THE_HERMIT]: [img('09-TheHermit.jpg')],
        [ArcanaIdentities.FORTUNE]: [img('10-WheelOfFortune.jpg')],
        [ArcanaIdentities.THE_HANGED_MAN]: [img('12-TheHangedMan.jpg')],
        [ArcanaIdentities.DEATH]: [img('13-Death.jpg')],
        [ArcanaIdentities.ART]: [img('14-Temperance.jpg')],
        [ArcanaIdentities.THE_DEVIL]: [img('15-TheDevil.jpg')],
        [ArcanaIdentities.THE_TOWER]: [img('16-TheTower.jpg')],
        [ArcanaIdentities.THE_STAR]: [img('17-TheStar.jpg')],
        [ArcanaIdentities.THE_MOON]: [img('18-TheMoon.jpg')],
        [ArcanaIdentities.THE_SUN]: [img('19-TheSun.jpg')],
        [ArcanaIdentities.THE_AEON]: [img('20-Judgement.jpg')],
        [ArcanaIdentities.THE_UNIVERSE]: [img('21-TheWorld.jpg')],

        /* Cups. */
        [ArcanaIdentities.ACE_OF_CUPS]: [img('Cups01.jpg')],
        [ArcanaIdentities.LOVE]: [img('Cups02.jpg')],
        [ArcanaIdentities.ABUNDANCE]: [img('Cups03.jpg')],
        [ArcanaIdentities.LUXURY]: [img('Cups04.jpg')],
        [ArcanaIdentities.DISAPPOINTMENT]: [img('Cups05.jpg')],
        [ArcanaIdentities.PLEASURE]: [img('Cups06.jpg')],
        [ArcanaIdentities.DEBAUCH]: [img('Cups07.jpg')],
        [ArcanaIdentities.INDOLENCE]: [img('Cups08.jpg')],
        [ArcanaIdentities.HAPPINESS]: [img('Cups09.jpg')],
        [ArcanaIdentities.SATIETY]: [img('Cups10.jpg')],
        [ArcanaIdentities.PRINCESS_OF_CUPS]: [img('Cups11.jpg')],
        [ArcanaIdentities.PRINCE_OF_CUPS]: [img('Cups12.jpg')],
        [ArcanaIdentities.QUEEN_OF_CUPS]: [img('Cups13.jpg')],
        [ArcanaIdentities.KNIGHT_OF_CUPS]: [img('Cups14.jpg')],

        /* Pentacles. */
        [ArcanaIdentities.ACE_OF_DISKS]: [img('Pentacles01.jpg')],
        [ArcanaIdentities.CHANGE]: [img('Pentacles02.jpg')],
        [ArcanaIdentities.WORKS]: [img('Pentacles03.jpg')],
        [ArcanaIdentities.POWER]: [img('Pentacles04.jpg')],
        [ArcanaIdentities.WORRY]: [img('Pentacles05.jpg')],
        [ArcanaIdentities.SUCCESS]: [img('Pentacles06.jpg')],
        [ArcanaIdentities.FAILURE]: [img('Pentacles07.jpg')],
        [ArcanaIdentities.PRUDENCE]: [img('Pentacles08.jpg')],
        [ArcanaIdentities.GAIN]: [img('Pentacles09.jpg')],
        [ArcanaIdentities.WEALTH]: [img('Pentacles10.jpg')],
        [ArcanaIdentities.PRINCESS_OF_DISKS]: [img('Pentacles11.jpg')],
        [ArcanaIdentities.PRINCE_OF_DISKS]: [img('Pentacles12.jpg')],
        [ArcanaIdentities.QUEEN_OF_DISKS]: [img('Pentacles13.jpg')],
        [ArcanaIdentities.KNIGHT_OF_DISKS]: [img('Pentacles14.jpg')],

        /* Wands. */
        [ArcanaIdentities.ACE_OF_WANDS]: [img('Wands01.jpg')],
        [ArcanaIdentities.DOMINION]: [img('Wands02.jpg')],
        [ArcanaIdentities.VIRTUE]: [img('Wands03.jpg')],
        [ArcanaIdentities.COMPLETION]: [img('Wands04.jpg')],
        [ArcanaIdentities.STRIFE]: [img('Wands05.jpg')],
        [ArcanaIdentities.VICTORY]: [img('Wands06.jpg')],
        [ArcanaIdentities.VALOUR]: [img('Wands07.jpg')],
        [ArcanaIdentities.SWIFTNESS]: [img('Wands08.jpg')],
        [ArcanaIdentities.STRENGTH]: [img('Wands09.jpg')],
        [ArcanaIdentities.OPPRESSION]: [img('Wands10.jpg')],
        [ArcanaIdentities.PRINCESS_OF_WANDS]: [img('Wands11.jpg')],
        [ArcanaIdentities.PRINCE_OF_WANDS]: [img('Wands12.jpg')],
        [ArcanaIdentities.QUEEN_OF_WANDS]: [img('Wands13.jpg')],
        [ArcanaIdentities.KNIGHT_OF_WANDS]: [img('Wands14.jpg')],

        /* Swords. */
        [ArcanaIdentities.ACE_OF_SWORDS]: [img('Swords01.jpg')],
        [ArcanaIdentities.PEACE]: [img('Swords02.jpg')],
        [ArcanaIdentities.SORROW]: [img('Swords03.jpg')],
        [ArcanaIdentities.TRUCE]: [img('Swords04.jpg')],
        [ArcanaIdentities.DEFEAT]: [img('Swords05.jpg')],
        [ArcanaIdentities.SCIENCE]: [img('Swords06.jpg')],
        [ArcanaIdentities.FUTILITY]: [img('Swords07.jpg')],
        [ArcanaIdentities.INTERFERENCE]: [img('Swords08.jpg')],
        [ArcanaIdentities.CRUELTY]: [img('Swords09.jpg')],
        [ArcanaIdentities.RUIN]: [img('Swords10.jpg')],
        [ArcanaIdentities.PRINCESS_OF_SWORDS]: [img('Swords11.jpg')],
        [ArcanaIdentities.PRINCE_OF_SWORDS]: [img('Swords12.jpg')],
        [ArcanaIdentities.QUEEN_OF_SWORDS]: [img('Swords13.jpg')],
        [ArcanaIdentities.KNIGHT_OF_SWORDS]: [img('Swords14.jpg')],
    },
    descriptions: {
        /* ---- Majors ---- */
        THE_FOOL: {
            title: '0 · The Fool',
            body: 'A youth steps blithely from a cliff, white rose in hand, a small dog leaping at his heels — the sun high, the abyss unexamined. Numbered zero: pure potential before the journey\u2019s first lesson. Trust the leap; the path forms beneath the foot that dares.',
        },
        THE_MAGUS: {
            title: 'I · The Magician',
            body: 'A figure at the altar raises the wand of will while cup, pentacle and sword wait in elemental order on the table — the lemniscate blazing above his head. As above, so below: all four suits are in his reach. Focus the will; manifest it through the tool at hand.',
        },
        THE_PRIESTESS: {
            title: 'II · The High Priestess',
            body: 'Between the black and white pillars B and J she sits, scroll half-hidden in her robes, the crescent moon at her feet and the veil of the temple behind. She is the silent depth behind all knowing. The answer you seek is behind the veil — be still enough to hear it.',
        },
        THE_EMPRESS: {
            title: 'III · The Empress',
            body: 'Seated in a wheat field by the river of Venus, crowned with twelve stars — growth, nurture and creation in their most maternal form. Everything she presides over ripens. Tend, feed, and let flourish: the garden obeys her patience.',
        },
        THE_EMPEROR: {
            title: 'IIII · The Emperor',
            body: 'A stern father on a stone throne, rams\u2019 heads at his sides — authority, structure and the will that builds boundaries. Order is his instrument and his gift. Take the throne of your own life; rule it justly.',
        },
        THE_HIEROPHANT: {
            title: 'V · The Hierophant',
            body: 'The triple-crowned teacher blesses his two ministers between the pillars of the visible church — tradition, lineage and the keys of the mysteries, one gold, one silver. Learning transmitted through structure. Take the hand of the teaching; keep your own conscience as final judge.',
        },
        THE_LOVERS: {
            title: 'VI · The Lovers',
            body: 'Adam and Eve stand before the archangel Raphael, the tree of knowledge coiled by the serpent behind them — union, choice and the awakening of self-consciousness in one scene. This is a card of decision as much as devotion. Choose with your whole being.',
        },
        THE_CHARIOT: {
            title: 'VII · The Chariot',
            body: 'A star-crowned rider stands motionless in his cube of a chariot, drawn by one black and one white sphinx that no rein controls — opposites harnessed by will alone. Victory through mastery of contrary forces. Hold your centre and the vehicle moves.',
        },
        ADJUSTMENT: {
            title: 'XI · Justice',
            body: 'Seated between the pillars of law, sword upright in one hand and scales perfectly level in the other — truth measured without mercy or malice. In the Smith deck Justice stands at eleven, watching your every transaction this year. Act so that the scales need not move.',
        },
        THE_HERMIT: {
            title: 'VIIII · The Hermit',
            body: 'A grey-cloaked elder stands alone on a snow peak, his lantern lifted — inside it, the six-pointed star of the higher will. He has climbed past the noise to find the light he now carries. Retreat, look within, and when you have found the lamp, hold it where others may see.',
        },
        FORTUNE: {
            title: 'X · Wheel of Fortune',
            body: 'The great wheel turns in the clouds, T-A-R-O spelled around its rim, the sphinx aloft, Hermanubis rising, Typhon falling — the eternal cycle that raises and lowers all things. Nothing stays at the top; nothing stays at the bottom. Read your position on the wheel and move with the turn.',
        },
        LUST: {
            title: 'VIII · Strength',
            body: 'A garlanded maiden closes — gently, without fear — the jaws of a great lion, the lemniscate of infinity above her head. This is the position the deck inherited from the Sola Busca: Fortitude at eight. True strength is the taming of the beast by gentleness, not its slaughter.',
        },
        THE_HANGED_MAN: {
            title: 'XII · The Hanged Man',
            body: 'A figure hangs serene from the world-tree by one foot, arms folded behind, a halo about his head — suspended, sacrificed, and seeing everything upside down. What looks like defeat is a voluntary change of angle. Let go of struggling; the vision you need arrives only from inversion.',
        },
        DEATH: {
            title: 'XIII · Death',
            body: 'The white horse carries the skeleton rider bearing the black banner — king and child alike before him, and beyond, the sun rising between two towers. An ending that is a doorway, nothing more and nothing less. Release what is finished; transformation follows on its heels.',
        },
        ART: {
            title: 'XIIII · Temperance',
            body: 'A winged figure stands one foot on stone and one in the pool, pouring water between two cups in an endless figure — the great work of blending, measured without spill. Equilibrium, patience, the middle way. Mix your opposites with care; the elixir is in the proportion.',
        },
        THE_DEVIL: {
            title: 'XV · The Devil',
            body: 'The horned goat of Mendes enthroned in darkness, chaining two figures who — look closely — could free themselves by choice. Bondage of the appetites, of matter, of the stories we agree to be bound by. Name the chain; the lock was never fastened.',
        },
        THE_TOWER: {
            title: 'XVI · The Tower',
            body: 'Lightning strikes the crowned tower; flame and falling figures tumble from its height as the crown itself is knocked loose. Built on false premises, nothing stands. Let the structure fall — the fire clears the ground for something true.',
        },
        THE_STAR: {
            title: 'XVII · The Star',
            body: 'A naked maiden kneels by the pool beneath one great eight-pointed star and seven lesser ones, pouring water on the land and into the water — hope renewed after the Tower\u2019s ruin. Inspiration returns from an open sky. Pour yourself out freely; the source is not in you alone.',
        },
        THE_MOON: {
            title: 'XVIII · The Moon',
            body: 'The moon drops its dew over a dark path winding between two towers, past dog and wolf howling at the disc, a crayfish rising from the pool. The country of dream, illusion and the unknown that still must be walked. Go carefully; the path leads through, not around, the dark.',
        },
        THE_SUN: {
            title: 'XVIIII · The Sun',
            body: 'A naked child rides a white horse beneath the blazing sun, wall and sunflowers behind — joy without shadow, vitality in its simplest clothes. Clarity, success, the warmth that makes growth effortless. Come out; the day is yours.',
        },
        THE_AEON: {
            title: 'XX · Judgement',
            body: 'The archangel\u2019s trumpet sounds over the sea, and the dead rise from open tombs with arms outspread — the great awakening, the call that cannot be ignored. Every part of your life you buried returns to be weighed and redeemed. Answer the call; rise to the larger life.',
        },
        THE_UNIVERSE: {
            title: 'XXI · The World',
            body: 'Within the laurel wreath of victory a dancing figure holds twin wands, the four living creatures watching from the corners — the dance complete, the journey closed and begun. Completion, integration, the world as your reward. Finish the cycle; take a bow; the next turn begins.',
        },

        /* ---- Cups ---- */
        ACE_OF_CUPS: {
            title: 'Ace of Cups',
            body: 'A hand from a cloud offers the chalice overflowing with five streams, the dove descending with the wafer of the host — love in its first, unconditioned pouring. The wellspring of feeling, offered before it is asked for. Receive it; the cup is given, not taken.',
        },
        LOVE: {
            title: 'Two of Cups',
            body: 'A man and woman face one another, each cup raised to the other, the lion-headed caduceus of Hermes rising between them — union recognised and answered. Reciprocity as a sacred pact. Meet it honestly, and it sustains itself.',
        },
        ABUNDANCE: {
            title: 'Three of Cups',
            body: 'Three figures dance in a garden harvest, cups raised together — friendship, community, joy with witnesses. Abundance multiplies by being shared. Call the circle; raise the cup.',
        },
        LUXURY: {
            title: 'Four of Cups',
            body: 'A youth sits under a tree, arms crossed, while a hand offers a cup he does not see — comfort satiated to the point of blindness. The gentle danger of too much sweetness. Look up; the offered cup is still there.',
        },
        DISAPPOINTMENT: {
            title: 'Five of Cups',
            body: 'A cloaked figure stares at three spilled cups; two stand full behind, unnoticed in the grief. Loss is real — so is what remains. When you can, turn around.',
        },
        PLEASURE: {
            title: 'Six of Cups',
            body: 'Children play in an old garden, exchanging cups of flowers — nostalgia, innocence, kindness returned from the past. Not every gift is a lesson; some are simply sweet. Let it be what it is.',
        },
        DEBAUCH: {
            title: 'Seven of Cups',
            body: 'A silhouette dreams before seven cups floating in the clouds, each holding a head, a serpent, treasure, a shrouded figure — fantasy multiplied, choices untested. Not all that glitters in the clouds exists. Choose one vision and test it against the ground.',
        },
        INDOLENCE: {
            title: 'Eight of Cups',
            body: 'A figure walks away by moonlight from eight cups neatly stacked — leaving behind what once looked complete. Walking out is sometimes the truest act of the heart. Go where the water still moves.',
        },
        HAPPINESS: {
            title: 'Nine of Cups',
            body: 'The wish-card: a satisfied host sits arms-folded before nine golden cups arched on a draped table. Emotional completion — the wish made warm and real. Accept it without negotiating.',
        },
        SATIETY: {
            title: 'Ten of Cups',
            body: 'The rainbow arcs over a home where two figures gesture in joy, ten cups shining in the sky — the heart\u2019s work completed, peace made visible. Fullness that asks only to be kept. Rest here; this is the home the heart meant.',
        },
        PRINCESS_OF_CUPS: {
            title: 'Page of Cups',
            body: 'A dreamy page regards the fish that leaps from his cup — the unexpected messenger of the heart, intuition in its apprentice form. A creative or emotional surprise is hatching. Take it seriously; it is small, not false.',
        },
        PRINCE_OF_CUPS: {
            title: 'Knight of Cups',
            body: 'The romantic knight rides slowly, cup extended, cloak winged like a swan — feeling in motion, the offer of the heart without guarantee. Beauty, invitation, diplomacy. Follow for beauty; the answer is not his to command.',
        },
        QUEEN_OF_CUPS: {
            title: 'Queen of Cups',
            body: 'The queen sits at the water\u2019s edge gazing into a closed chalice — feeling in its purest element, psychically open, deeply empathic. Her gift is total receptivity; her discipline is keeping her own shape within it. Trust what arrives through her.',
        },
        KNIGHT_OF_CUPS: {
            title: 'King of Cups',
            body: 'The king thrones calm upon a stormy sea, cup held steady, a ship and a dolphin behind — maturity of feeling, love that has mastered its own waves. Compassion with a rudder. Bring your turbulence to him; he will not add to it.',
        },

        /* ---- Pentacles ---- */
        ACE_OF_DISKS: {
            title: 'Ace of Pentacles',
            body: 'A hand from a cloud offers the golden pentacle over a garden gate and white lily — matter in its first seed, opportunity made tangible. Hands, not visions: plant something real.',
        },
        CHANGE: {
            title: 'Two of Pentacles',
            body: 'A youth dances between two pentacles bound by the lemniscate ribbon, ships rolling behind — harmonious flux, life kept in playful balance. Nothing is fixed and nothing falls. The skill is the rhythm; keep dancing.',
        },
        WORKS: {
            title: 'Three of Pentacles',
            body: 'The mason works his bench before two robed masters in a cathedral half-born — skilled labour recognised and blessed. Collaboration, craft, the designed thing rising. Build it properly; it will outlast the building.',
        },
        POWER: {
            title: 'Four of Pentacles',
            body: 'The crowned figure clutches a pentacle to his breast, two more beneath his feet, one upon his crown — control held so tightly it becomes a fortress. Stability is real; so is its price. A fortress is only useful if it has a gate.',
        },
        WORRY: {
            title: 'Five of Pentacles',
            body: 'Two ragged figures pass the lit church window, out in the snow — material want, and the help left unentered. The door is open; the night is cold but temporary. Ask. Walk in.',
        },
        SUCCESS: {
            title: 'Six of Pentacles',
            body: 'The merchant weighs out coins with exact scales, giving to one side and — look — receiving on the other — generosity in balance, harvest shared. What is given from genuine plenty returns as genuine standing. Keep the scales honest.',
        },
        FAILURE: {
            title: 'Seven of Pentacles',
            body: 'The grower leans on his hoe before the full-fruiting vine — pause after long labour, assessment before harvest. What has grown is real; what comes next is a choice. Reassess honestly; some projects ask to be released, not rescued.',
        },
        PRUDENCE: {
            title: 'Eight of Pentacles',
            body: 'The apprentice hammers pentacle after pentacle into perfect shape, tools at his feet — skill as devotion, the slow compounding of craft. Mastery is daily practice. Pay in; the coin you shape is yourself.',
        },
        GAIN: {
            title: 'Nine of Pentacles',
            body: 'The elegant woman stands alone in her vineyard, falcon hooded on her glove — abundance earned and enjoyed in one\u2019s own company. Discipline fruiting into luxury. Walk your own garden; it is yours, and it is enough.',
        },
        WEALTH: {
            title: 'Ten of Pentacles',
            body: 'The patriarch sits amid arches and hounds while generations gather beneath coins arrayed in the Tree of Life — legacy, security, wealth that outlives the earner. Guard it as a well, not a wall.',
        },
        PRINCESS_OF_DISKS: {
            title: 'Page of Pentacles',
            body: 'The studious page holds his pentacle at eye level in the ploughed field — the student of the material world, practical beyond his years. A concrete opportunity is presenting itself for study. Look closely, then begin.',
        },
        PRINCE_OF_DISKS: {
            title: 'Knight of Pentacles',
            body: 'The heavy-armoured knight sits his heavy horse at a halt in the ripe field — the slowest rider in the deck, and the only one who always arrives. Routine, patience, unglamorous persistence. He wins by never stopping.',
        },
        QUEEN_OF_DISKS: {
            title: 'Queen of Pentacles',
            body: 'The queen thrones in a flowering garden, pentacle on her lap, roses at her feet — resourceful warmth, the nurture of the tangible. Bodies, food, home, harvest are her realm. Prosperity follows her like a season.',
        },
        KNIGHT_OF_DISKS: {
            title: 'King of Pentacles',
            body: 'The king thrones amid his vines, armoured and abundant, the bull-tamer of the material realm — provision, mastery, success made solid. He is the earner, the provider, the slow certain harvest. Prosperity is his native language.',
        },

        /* ---- Wands ---- */
        ACE_OF_WANDS: {
            title: 'Ace of Wands',
            body: 'A hand from a cloud offers the sprouting staff over a castle-cleared plain — will in its first ignition, creativity pregnant with every possible act. A spark this pure becomes illumination or wildfire. Choose the direction; plant it.',
        },
        DOMINION: {
            title: 'Two of Wands',
            body: 'The planter stands on his battlement, globe in hand, gazing over sea and land while the second staff is fixed — the world surveyed by a will deciding where to strike. Dominion is initiative at its most confident. The plan is drawn; the world will answer.',
        },
        VIRTUE: {
            title: 'Three of Wands',
            body: 'The figure watches his ships sail from the high ground where three staves stand planted — enterprise launched and awaiting returns. Strength born of alignment compounds. Keep facing the horizon you chose.',
        },
        COMPLETION: {
            title: 'Four of Wands',
            body: 'The garlanded bridge of four staves, dancers beneath, and the greeting at the gate — celebration of a milestone, homecoming, harmony established. Rest is part of the building. Let this be a genuine resting point.',
        },
        STRIFE: {
            title: 'Five of Wands',
            body: 'Five youths clash staves in mock-yet-serious battle — competing energies, friction with no clear enemy. The struggle is the examination, not the failure. Fight cleanly; learn what you actually want.',
        },
        VICTORY: {
            title: 'Six of Wands',
            body: 'The victor rides through the cheering gate, laurel-wreathed, staff crowned — triumph after genuine struggle, success earned in public. Wear it outwardly as generosity; the crown fits because the head has grown.',
        },
        VALOUR: {
            title: 'Seven of Wands',
            body: 'The defender on high ground beats back six lower staves with his seventh — courage under sustained pressure, position held against odds. The odds are real, and so is the strength. Hold the line.',
        },
        SWIFTNESS: {
            title: 'Eight of Wands',
            body: 'Eight staves fly arrow-like over open country toward the distant shore — energy unbound, communication at the speed of need. Events accelerate beyond planning. Align loosely and steer on the wing.',
        },
        STRENGTH: {
            title: 'Nine of Wands',
            body: 'The bandaged guard, head wrapped, grips his staff over the eight behind him — immense force in conscious reserve, wary but unbroken. Resilience is the discipline of the almost-beaten who refuse to be. Do not mistake restraint for weakness.',
        },
        OPPRESSION: {
            title: 'Ten of Wands',
            body: 'The figure bends double beneath ten staves gathered in his arms, the village still far ahead — will turned burden, energy enslaved to ends that no longer justify the cost. Put the burden down and ask what it was for.',
        },
        PRINCESS_OF_WANDS: {
            title: 'Page of Wands',
            body: 'The flamboyant youth salutes with his sprouting staff in a sunlit desert — fire finding a foothold, adventure announcing itself. Impulsive, magnetic, entirely ready to begin. Her gift is beginnings; her lesson is the hearth.',
        },
        PRINCE_OF_WANDS: {
            title: 'Knight of Wands',
            body: 'The knight charges, staff held like a lance, salamanders embroidered on his surcoat — will given direction and speed, the charioteer of fire. Decisive, intense, dramatic; his flame illuminates or consumes. Ride with him for the beginning, never the follow-through.',
        },
        QUEEN_OF_WANDS: {
            title: 'Queen of Wands',
            body: 'The queen thrones with sunflower and black cat, wand in hand — vibrant charisma, self-assurance that needs no audience. Warmth that transforms; passion that inspires devotion. The strongest flame is the one that lights others.',
        },
        KNIGHT_OF_WANDS: {
            title: 'King of Wands',
            body: 'The king thrones upright, wand held like a living thing, lion at his side — fire in its most unmediated form: daring, magnificent, total. Adventure incarnate. Follow him for the beginning, never for the follow-through.',
        },

        /* ---- Swords ---- */
        ACE_OF_SWORDS: {
            title: 'Ace of Swords',
            body: 'The hand from the cloud offers the upright sword crowned with the victory wreath, the pendulum hanging true — clarity as an event, the first cut of the mind. What it severs cannot be rejoined. Take it knowingly.',
        },
        PEACE: {
            title: 'Two of Swords',
            body: 'The blindfolded figure sits between two seas, arms crossed upon the balanced swords — equilibrium held by refusing to look. A fragile, real truce. Decide nothing until the moon is full; then decide.',
        },
        SORROW: {
            title: 'Three of Swords',
            body: 'The heart pierced by three blades under driving rain — grief, separation, truth arriving where it hurts. This card needs no decoration and offers no bypass. Let it cut cleanly; a clean wound heals true.',
        },
        TRUCE: {
            title: 'Four of Swords',
            body: 'The effigy rests in the chapel window, hands in prayer, swords hung beneath — rest from conflict without resolution of it. Recuperation is not surrender. Use the interval to strengthen; peace held lightly is peace kept.',
        },
        DEFEAT: {
            title: 'Five of Swords',
            body: 'The victor gathers the swords while the defeated walk away and the clouds mirror the misery — a win that costs the frame it stood in. Curiously self-inflicted. Withdraw with what dignity remains; the assets are not all lost.',
        },
        SCIENCE: {
            title: 'Six of Swords',
            body: 'The ferryman poles the robed family across calm water to the far shore, six swords standing in the boat — passage out of trouble, mind as instrument, movement with method. Problems here yield to precision. Cross the water.',
        },
        FUTILITY: {
            title: 'Seven of Swords',
            body: 'The figure slips from the camp with five swords, leaving two behind — ingenuity without stamina, the clever manoeuvre that solves less than it steals. The dim strategy at three in the morning. Stop pushing; reopen the windows.',
        },
        INTERFERENCE: {
            title: 'Eight of Swords',
            body: 'The bound, blindfolded woman stands among eight planted swords on the marsh — trapped less by the blades than by the story of them. The ropes are looser than they feel. Prune one story and walk out.',
        },
        CRUELTY: {
            title: 'Nine of Swords',
            body: 'The figure sits face in hands upon the bed, nine swords on the wall, the night long — anguish, obsession, the mind at war with itself. Most of these blades are hung, not thrown. The mercy is waking.',
        },
        RUIN: {
            title: 'Ten of Swords',
            body: 'The figure lies face-down, ten swords in the back, the sky black above and dawn breaking at the horizon — the absolute bottom, written out completely. Nothing grows on dishonest foundations; and the sun is already rising. Ground zero is also ground.',
        },
        PRINCESS_OF_SWORDS: {
            title: 'Page of Swords',
            body: 'The vigilant youth stands on the windswept height, sword raised, clouds streaming — perception sharpened to a point, watching for the first movement. Curiosity with an edge. Cut through comfort to fact; then sheathe it wisely.',
        },
        PRINCE_OF_SWORDS: {
            title: 'Knight of Swords',
            body: 'The knight charges full-tilt into the wind, sword level, cloaks streaming — thought as cavalry charge, intellect weaponised and released. Relentless, swift, absolute. Deploy him where the truth must arrive on time.',
        },
        QUEEN_OF_SWORDS: {
            title: 'Queen of Swords',
            body: 'The queen in profile thrones beneath the raised sword, her other hand extended — clarity carved by loss, feeling refined into sight. Unbowed, impartial, precise. She extends the sword as justice: exactly as far as required, and no farther.',
        },
        KNIGHT_OF_SWORDS: {
            title: 'King of Swords',
            body: 'The king thrones beneath a clear sky, blade upright in his hand — intellect enthroned, judgement given body. He is right more often than he is kind. Bring him the truth; he will do the rest.',
        },
    },
};
